import React, { useEffect, useState } from "react";
import { MapPin, Building, ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

/* 🔔 TOAST */
const Toast = ({ message, type }) => (
  <div className={`fixed top-6 right-6 px-4 py-3 rounded-xl shadow-lg text-white z-50
    ${type === "success" ? "bg-green-500" : type === "error" ? "bg-red-500" : "bg-blue-500"}`}>
    {message}
  </div>
);

const JobDetails = () => {
  const { id } = useParams();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);
  const [checking, setChecking] = useState(true);
  const [applying, setApplying] = useState(false); // ✅ NEW
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role")?.toLowerCase(); // ✅ FIX

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* FETCH JOB */
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await fetch(`https://digital-worker-exchange.onrender.com/api/jobs/${id}`);
        const data = await res.json();
        setJob(data);
      } catch {
        showToast("Failed to load job", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  /* CHECK APPLIED */
  useEffect(() => {
    const checkApplied = async () => {
      if (!token || role !== "worker" || !job?._id) {
        setChecking(false);
        return;
      }

      try {
        const res = await fetch(
          `https://digital-worker-exchange.onrender.com/api/applications/check/${job._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setApplied(data.applied);
      } catch {}
      setChecking(false);
    };

    checkApplied();
  }, [job]);

  /* APPLY */
  const handleApply = async () => {
    if (!token) {
      showToast("Login required to apply", "error");
      return;
    }

    if (role !== "worker") {
      showToast("Only workers can apply", "error");
      return;
    }

    // 🔥 OPTIMISTIC UI
    setApplying(true);
    setApplied(true);

    try {
      const res = await fetch(
        "https://digital-worker-exchange.onrender.com/api/applications/apply",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId: job._id }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        // 🔥 HANDLE ALREADY APPLIED
        if (data.message?.toLowerCase().includes("already")) {
          setApplied(true);
          showToast("Already applied to this job", "error");
        } else {
          setApplied(false); // rollback
          showToast(data.message || "Failed to apply", "error");
        }
        return;
      }

      showToast("Successfully applied! Recruiter will contact you soon.");
    } catch {
      setApplied(false);
      showToast("Something went wrong", "error");
    } finally {
      setApplying(false);
    }
  };

  /* PREMIUM FALLBACK CONTENT */
  const getDetails = () => ({
    overview:
      job.description ||
      `We are looking for a skilled ${job.trade} to join immediately. This role involves hands-on work, reliability, and professionalism.`,

    responsibilities: [
      `Perform ${job.trade} related tasks efficiently`,
      "Maintain quality and safety standards",
      "Communicate with employer professionally",
      "Complete tasks within given deadlines",
    ],

    requirements: [
      `${job.trade} experience preferred`,
      "Basic communication skills",
      "Ability to work on-site",
      "Punctual and responsible",
    ],

    benefits: [
      "Direct employer connection (no middleman)",
      "Daily/weekly payments",
      "Flexible work opportunities",
      "Long-term hiring potential",
    ],
  });

  if (loading) return <div className="min-h-screen flex justify-center items-center">Loading...</div>;
  if (!job) return <div className="min-h-screen flex justify-center items-center">Error loading job</div>;

  const details = getDetails();

  const disabled = checking || applying || role !== "worker";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 pt-24 px-6 pb-28">

      {toast && <Toast message={toast.msg} type={toast.type} />}

      <div className="max-w-6xl mx-auto mb-6">
        <Link to="/findwork" className="flex items-center gap-2 text-gray-600 hover:text-black">
          <ArrowLeft size={20} /> Back
        </Link>
      </div>

      <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">

        {/* LEFT CONTENT (UNCHANGED) */}
        <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow border">
          <h1 className="text-3xl font-bold text-gray-900">{job.title}</h1>

          <p className="text-gray-600 mt-1">
            {job.employer?.name || "Employer"} · Verified
          </p>

          <div className="flex gap-2 mt-4 flex-wrap">
            <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">{job.trade}</span>
            <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-sm">Immediate Join</span>
          </div>

          <Section title="Overview" content={details.overview} />
          <ListSection title="Responsibilities" items={details.responsibilities} />
          <ListSection title="Requirements" items={details.requirements} />
          <ListSection title="Benefits" items={details.benefits} />
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="space-y-6">

          <div className="bg-white p-6 rounded-2xl border shadow">
            <p className="text-sm text-gray-500">Salary</p>
            <p className="text-xl font-bold text-emerald-600">
              ₹{job.minPay} – ₹{job.maxPay}
            </p>

            <p className="text-sm text-gray-500 mt-4">Location</p>
            <p className="flex items-center gap-1 text-gray-700">
              <MapPin size={16} /> {job.location}
            </p>
          </div>

          {/* 🔥 UPDATED BUTTON ONLY */}
          <button
            onClick={handleApply}
            disabled={disabled || applied}
            className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2
              ${
                applied
                  ? "bg-green-500 text-white cursor-not-allowed"
                  : role !== "worker"
                  ? "bg-gray-200 text-gray-500"
                  : "bg-gradient-to-r from-blue-600 to-emerald-500 text-white"
              }`}
          >
            {checking
              ? "Checking..."
              : applying
              ? <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></span>
              : applied
              ? "Applied ✓"
              : "Apply Now"}
          </button>

        </div>
      </div>
    </div>
  );
};

/* COMPONENTS */

const Section = ({ title, content }) => (
  <div className="mt-8">
    <h2 className="text-xl font-semibold">{title}</h2>
    <p className="text-gray-600 mt-2">{content}</p>
  </div>
);

const ListSection = ({ title, items }) => (
  <div className="mt-8">
    <h2 className="text-xl font-semibold">{title}</h2>
    <ul className="mt-2 space-y-2 text-gray-600">
      {items.map((item, i) => (
        <li key={i}>• {item}</li>
      ))}
    </ul>
  </div>
);

export default JobDetails;